'use client';
import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import CheckInHero from './components/CheckInHero';
import GuidedPromptBanner from './components/GuidedPromptBanner';
import MoodTrendChart from './components/MoodTrendChart';
import DiaryFeed from './components/DiaryFeed';
import StreakStats from './components/StreakStats';
import WellbeingPopup from './components/WellbeingPopup';
import RwandaComplianceBanner from './components/RwandaComplianceBanner';
import RealtimeCheckInBadge from './components/RealtimeCheckInBadge';
import WeeklySummaryReport from './components/WeeklySummaryReport';
import SafeGamblingAdvisory from './components/SafeGamblingAdvisory';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { hasCompletedOnboarding } from '@/lib/onboarding';

export default function DailyCheckInPage() {
  const { t } = useLanguage();
  const { user, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && user && !hasCompletedOnboarding()) {
      router?.push('/onboarding');
    }
  }, [user, loading, router]);

  return (
    <AppLayout>
      <div className="space-y-5 fade-in">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-700 text-foreground">{t('home.title')}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t('home.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-sm font-600 px-3 py-1.5 rounded-full self-start sm:self-auto">
              <span>🔥</span>
              <span>14 {t('home.streak')}</span>
            </span>
            <RealtimeCheckInBadge />
          </div>
        </div>

        {/* Rwanda compliance banner */}
        <RwandaComplianceBanner />

        {/* Guided prompt */}
        <GuidedPromptBanner />

        {/* Check-in form */}
        <CheckInHero />

        {/* Stats row */}
        <StreakStats />

        {/* Weekly summary */}
        <WeeklySummaryReport />

        {/* Chart + feed */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
          <div className="xl:col-span-3">
            <MoodTrendChart />
          </div>
          <div className="xl:col-span-2">
            <DiaryFeed />
          </div>
        </div>

        {/* Safe gambling advisory */}
        <SafeGamblingAdvisory />
      </div>

      {/* Wellbeing popup nudges */}
      <WellbeingPopup />
    </AppLayout>
  );
}